import moment from "moment";
import { TChangedMeal, TMealKey, TUnmet } from "./types";

export const MEAL_KEYS: TMealKey[] = ["B", "AM", "L", "PM"];

export const MEAL_NAME: { [k: string]: string } = {
  B: "Breakfast",
  AM: "AM Snack",
  L: "Lunch",
  PM: "PM Snack",
};
export const MEAL_LOWER: { [k: string]: string } = {
  B: "breakfast",
  AM: "AM snack",
  L: "lunch",
  PM: "PM snack",
};
export const MEAL_PLURAL: { [k: string]: string } = {
  B: "breakfasts",
  AM: "AM snacks",
  L: "lunches",
  PM: "PM snacks",
};

export const DIETS: [string, string][] = [
  ["std", "Standard"],
  ["veg", "Vegetarian"],
  ["vgn", "Vegan"],
  ["gf", "Gluten-free"],
  ["df", "Dairy-free"],
  ["gfdf", "Gluten-free & dairy-free"],
  ["egg", "Egg allergy"],
];

const NEED_WORD: { [k: string]: string } = {
  std: "standard",
  veg: "vegetarian",
  vgn: "vegan",
  gf: "gluten-free",
  df: "dairy-free",
  gfdf: "gluten-free & dairy-free",
  egg: "egg-allergy",
};

export const WEEKDAYS: [string, string][] = [
  ["1", "Mon"],
  ["2", "Tue"],
  ["3", "Wed"],
  ["4", "Thu"],
  ["5", "Fri"],
];

/** "2026-10-21" -> "Wed 21 Oct" */
export const shortDate = (date: string) => moment(date, "YYYY-MM-DD").format("ddd D MMM");

export const listAnd = (items: string[]) =>
  items.length < 2
    ? items.join("")
    : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

/** The server's meal time names look like "3) Lunch" or "3) Lunch Allergy". */
export const mealKeyOfMealTime = (name: string): TMealKey | null => {
  if (/sack/i.test(name)) return null;
  if (/\bBreakfast\b/i.test(name)) return "B";
  if (/\bAM Snack\b/i.test(name)) return "AM";
  if (/\bPM Snack\b/i.test(name)) return "PM";
  if (/\bLunch\b/i.test(name)) return "L";
  return null;
};

export const isAllergyMealTime = (name: string) => /allergy/i.test(name);

/** "No dish on today's menu suits 1 egg-allergy child and 2 vegan children — please choose for them." */
export const needsYouText = (unmet: TUnmet[]) =>
  `No dish on today's menu suits ${listAnd(
    unmet.map(
      (u) =>
        `${u.count} ${NEED_WORD[u.need] || u.need} ${
          u.count === 1 ? "child" : "children"
        }`
    )
  )} \u2014 please choose for them.`;

/** The dialog sentence shown when hand-changed meals will not be updated. */
export const keepNumbersText = (daysToUpdate: number, changed: TChangedMeal[]) => {
  const n = changed.length;
  const meals = changed
    .map((c) => c.meal)
    .filter((m, i, all) => all.indexOf(m) === i);
  const what =
    meals.length === 1
      ? n === 1
        ? MEAL_LOWER[meals[0]]
        : MEAL_PLURAL[meals[0]]
      : n === 1
      ? "meal"
      : "meals";
  const dates = changed.map(
    (c) => shortDate(c.date) + (meals.length > 1 ? ` (${MEAL_LOWER[c.meal]})` : "")
  );
  const lead =
    daysToUpdate === 0
      ? "No other days need updating."
      : `Your new numbers will update ${daysToUpdate} ${
          daysToUpdate === 1 ? "day" : "days"
        }.`;
  return `${lead} ${n} ${what} you changed by hand will keep ${
    n === 1 ? "its" : "their"
  } numbers: ${dates.join(", ")}.`;
};

/** The confirmation after Save (spec 017). Never says "0 days". */
export const savedMessage = (o: {
  enabled: boolean;
  days: number;
  kept: number;
  overwrite: boolean;
  roomName: string;
}): string => {
  const { enabled, days, kept, overwrite, roomName } = o;
  if (!enabled)
    return `Saved. The standing order is off for ${roomName}. Nothing new will be filled in.`;
  const keptText = `${kept} hand-changed ${kept === 1 ? "meal kept its numbers." : "meals kept their numbers."}`;
  if (kept && overwrite)
    return `Done. ${days === 1 ? "1 day now follows" : `All ${days} days now follow`} the new numbers, including the ${kept} you had changed by hand.`;
  if (kept)
    return days === 0
      ? `Saved. No other days needed updating. ${keptText}`
      : `Done. ${days} ${days === 1 ? "day" : "days"} updated. ${keptText}`;
  if (days === 0) return "Saved. Nothing needed updating.";
  return `Saved. ${days} ${days === 1 ? "day now follows" : "days now follow"} the new numbers.`;
};
